import React, { useCallback, useEffect, useState } from "react";
import type { DatePickerProps, FormInstance, TableColumnsType, TableProps } from "antd";
import useSWR from 'swr';
import { Alert, Form, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from "react-router-dom";

export interface DataTypeCOP {
    key: React.Key;
    contact_name: string;
    devision: string;
    job_title: string;
    phone_no: string;
    status: string;
}

export interface DataTypeBank {
    key: React.Key;
    bank_name: string;
    acc_type: string;
    account_no: string;
    name: string;
    status: string;
}

export interface ProvinsiData {
    kode_provinsi: string;
    nama_provinsi: string;
}

export interface KabupatenData {
    kode_dati: string;
    deskripsi_kode_dati: string;
}

export interface KecamatanData {
    kode_kecamatan: string;
    deskripsi_kode_kecamatan: string;
}

export interface KelurahanData {
    kode_kelurahan: string;
    deskripsi_kode_kelurahan: string;
    kode_pos: string;
}

export interface VatTransData {
    code: string;
    deskripsi: string;
}

export interface SupplierFullData extends SupplierDataTypeFromDB {
    term_of_payment: string | null;
    id_no: string | null;
    rt_rw: string | null;
    provinsi: string | null;
    kabupaten: string | null;
    kecamatan: string | null;
    kelurahan: string | null;
    kode_pos: string | null;
    email: string | null;
    phone: string | null;
    minimum_down_payment: number | null;
    tax_industry: number | null;
    discount: string | null;
    npwp: string | null;
    npwp_date: string | null;
    pkp: number;
    pkp_no: string | null;
    pkp_date: string | null;
    vat_transaction_code: string | null;
    vat_name: string | null;
    vat_address: string | null;
    vat_rt_rw: string | null;
    vat_provinsi: string | null;
    vat_kabupaten: string | null;
    vat_kecamatan: string | null;
    vat_kelurahan: string | null;
    vat_kode_pos: string | null;
    kpp_code: string | null;
    kpp_name: string | null;
    same_as_vat: number;
    income_tax_npwp: string | null;
    income_tax_npwp_date: string | null;
    income_tax_pkp: number;
    income_tax_pkp_no: string | null;
    income_tax_pkp_date: string | null;
    income_tax_name: string | null;
    income_tax_address: string | null;
    income_tax_rt_rw: string | null;
    income_tax_provinsi: string | null;
    income_tax_kabupaten: string | null;
    income_tax_kecamatan: string | null;
    income_tax_kelurahan: string | null;
    income_tax_kode_pos: string | null;
    income_tax_kpp_code: string | null;
    income_tax_kpp_name: string | null;

    nama_provinsi?: string;
    nama_kabupaten?: string;
    nama_kecamatan?: string;
    nama_kelurahan?: string;
    master_kode_pos?: string;
    vat_nama_provinsi?: string;
    vat_nama_kabupaten?: string;
    vat_nama_kecamatan?: string;
    vat_nama_kelurahan?: string;
    vat_master_kode_pos?: string;
    income_tax_nama_provinsi?: string;
    income_tax_nama_kabupaten?: string;
    income_tax_nama_kecamatan?: string;
    income_tax_nama_kelurahan?: string;
    income_tax_master_kode_pos?: string;

    cop_details?: DataTypeCOP[];
    bank_details?: DataTypeBank[];
}

export type { TableProps, TableColumnsType, DatePickerProps };
const fetcher = (url: string) => fetch(url).then((res) => res.json());

export interface SupplierMasterProps {
    transformStyle: React.CSSProperties;
    showContentProfiel: boolean;
    setShowContentProfiel: (show: boolean) => void;
    showContentStatus: boolean;
    setShowContentStatus: (show: boolean) => void;
    showContentVAT: boolean;
    setShowContentVAT: (show: boolean) => void;
    showContentIncomeTax: boolean;
    setShowContentIncomeTax: (show: boolean) => void;
    showContentContactorPIC: boolean;
    setShowContentContactorPIC: (show: boolean) => void;
    showContentBank: boolean;
    setShowContentBank: (show: boolean) => void;
    onDateChange: DatePickerProps['onChange'];
    onChangeSelect: (value: string) => void;
    selectedValuePKP: string | null;
    handleCheckboxChangePKP: (value: string) => void;
    selectedValuePKPvat: string | null;
    handleCheckboxChangePKPvat: (value: string) => void;
    columnsCOP: TableColumnsType<DataTypeCOP>;
    rowSelectionCOP: TableProps<DataTypeCOP>["rowSelection"];
    selectedRowKeysCOP: React.Key[];
    copData: DataTypeCOP[];
    columnsBank: TableColumnsType<DataTypeBank>;
    rowSelectionBank: TableProps<DataTypeBank>["rowSelection"];
    selectedRowKeysBank: React.Key[];
    bankData: DataTypeBank[];
    formInitialValues: { status?: string };
    nextSupplierCode?: string;
    isLoadingNextCode: boolean;
    errorNextCode?: any;
    form: FormInstance;
    provinsiOptions: { value: string; label: string; }[];
    isLoadingProvinsi: boolean;
    errorProvinsi?: any;
    onProvinsiChange: (value: string) => void;
    kabupatenOptions: { value: string; label: string; }[];
    isLoadingKabupaten: boolean;
    errorKabupaten?: any;
    selectedKodeProvinsi: string | null;
    onKabupatenChange: (value: string) => void;
    kecamatanOptions: { value: string; label: string; }[];
    isLoadingKecamatan: boolean;
    errorKecamatan?: any;
    selectedKodeDati: string | null;
    onKecamatanChange: (value: string) => void;
    kelurahanOptions: { value: string; label: string; }[];
    isLoadingKelurahan: boolean;
    errorKelurahan?: any;
    selectedKodeKecamatan: string | null;
    onKelurahanChange: (value: string) => void;
    onProvinsiChangeVAT: (value: string) => void;
    kabupatenOptionsVAT: { value: string; label: string; }[];
    isLoadingKabupatenVAT: boolean;
    errorKabupatenVAT?: any;
    selectedKodeProvinsiVAT: string | null;
    onKabupatenChangeVAT: (value: string) => void;
    kecamatanOptionsVAT: { value: string; label: string; }[];
    isLoadingKecamatanVAT: boolean;
    errorKecamatanVAT?: any;
    selectedKodeDatiVAT: string | null;
    onKecamatanChangeVAT: (value: string) => void;
    kelurahanOptionsVAT: { value: string; label: string; }[];
    isLoadingKelurahanVAT: boolean;
    errorKelurahanVAT?: any;
    selectedKodeKecamatanVAT: string | null;
    onKelurahanChangeVAT: (value: string) => void;
    onProvinsiChangeIncomeTax: (value: string) => void;
    kabupatenOptionsIncomeTax: { value: string; label: string; }[];
    isLoadingKabupatenIncomeTax: boolean;
    errorKabupatenIncomeTax?: any;
    selectedKodeProvinsiIncomeTax: string | null;
    onKabupatenChangeIncomeTax: (value: string) => void;
    kecamatanOptionsIncomeTax: { value: string; label: string; }[];
    isLoadingKecamatanIncomeTax: boolean;
    errorKecamatanIncomeTax?: any;
    selectedKodeDatiIncomeTax: string | null;
    onKecamatanChangeIncomeTax: (value: string) => void;
    kelurahanOptionsIncomeTax: { value: string; label: string; }[];
    isLoadingKelurahanIncomeTax: boolean;
    errorKelurahanIncomeTax?: any;
    selectedKodeKecamatanIncomeTax: string | null;
    onKelurahanChangeIncomeTax: (value: string) => void;
    vatTransOptions: { value: string; label: string; }[];
    isLoadingVatTrans: boolean;
    errorVatTrans?: any;
    isCopModalVisible: boolean;
    showCopModal: () => void;
    handleCopModalCancel: () => void;
    handleCopModalOk: (values: any) => void;
    handleCopRowDoubleClick: (record: DataTypeCOP) => void;
    isEditMode: boolean;
    editingCopRecord: DataTypeCOP | null;
    handleActivateCop: () => void;
    handleDeactivateCop: () => void;
    isCopActionButtonDisabled: boolean;
    isBankModalVisible: boolean;
    showBankModal: () => void;
    handleBankModalCancel: () => void;
    handleBankModalOk: (values: any) => void;
    handleBankRowDoubleClick: (record: DataTypeBank) => void;
    isBankEditMode: boolean;
    editingBankRecord: DataTypeBank | null;
    handleActivateBank: () => void;
    handleDeactivateBank: () => void;
    isBankActionButtonDisabled: boolean;
    handleSave: () => Promise<void>;
    isSaving: boolean;
    isEditModeMaster: boolean;
    supplierId?: string;
    isLoadingSupplierData: boolean;
    isKppModalVisible: boolean;
    showKppModal: (target: 'vat' | 'income_tax') => void;
    handleKppModalCancel: () => void;
    handleKppModalOk: (values: any) => void;
    kppTargetField: 'vat' | 'income_tax' | null;

}

interface SupplierDataTypeFromDB {
    id: number;
    supplier_code: string;
    supplier_name: string;
    address: string | null;
    status: number;
    status_text: string;
}


export const withSupplierMasterLogic = (WrappedComponent: React.ComponentType<SupplierMasterProps>) => {
    const EnhancedComponent: React.FC = (originalProps) => {

        const [form] = Form.useForm();
        const { supplierId } = useParams<{ supplierId: string }>();
        const isEditModeMaster = !!supplierId;
        const navigate = useNavigate();
        const [showContentProfiel, setShowContentProfiel] = useState(false);
        const [showContentStatus, setShowContentStatus] = useState(false);
        const [showContentVAT, setShowContentVAT] = useState(false);
        const [showContentIncomeTax, setShowContentIncomeTax] = useState(false);
        const [showContentContactorPIC, setShowContentContactorPIC] = useState(false);
        const [showContentBank, setShowContentBank] = useState(false);
        const [selectedValuePKP, setSelectedValuePKP] = useState<string | null>(null);
        const [selectedValuePKPvat, setSelectedValuePKPvat] = useState<string | null>(null);
        const [selectedRowKeysCOP, setSelectedRowKeysCOP] = useState<React.Key[]>([]);
        const [selectedRowKeysBank, setSelectedRowKeysBank] = useState<React.Key[]>([]);
        const [selectedKodeProvinsi, setSelectedKodeProvinsi] = useState<string | null>(null);
        const [selectedKodeDati, setSelectedKodeDati] = useState<string | null>(null);
        const [selectedKodeKecamatan, setSelectedKodeKecamatan] = useState<string | null>(null);
        const [allKelurahanData, setAllKelurahanData] = useState<KelurahanData[]>([]);
        const [selectedKodeProvinsiVAT, setSelectedKodeProvinsiVAT] = useState<string | null>(null);
        const [selectedKodeDatiVAT, setSelectedKodeDatiVAT] = useState<string | null>(null);
        const [selectedKodeKecamatanVAT, setSelectedKodeKecamatanVAT] = useState<string | null>(null);
        const [allKelurahanDataVAT, setAllKelurahanDataVAT] = useState<KelurahanData[]>([]);
        const [selectedKodeProvinsiIncomeTax, setSelectedKodeProvinsiIncomeTax] = useState<string | null>(null);
        const [selectedKodeDatiIncomeTax, setSelectedKodeDatiIncomeTax] = useState<string | null>(null);
        const [selectedKodeKecamatanIncomeTax, setSelectedKodeKecamatanIncomeTax] = useState<string | null>(null);
        const [allKelurahanDataIncomeTax, setAllKelurahanDataIncomeTax] = useState<KelurahanData[]>([]);
        const [isCopModalVisible, setIsCopModalVisible] = useState(false);
        const [copData, setCopData] = useState<DataTypeCOP[]>([]);
        const [editingCopRecord, setEditingCopRecord] = useState<DataTypeCOP | null>(null);
        const [isEditMode, setIsEditMode] = useState(false);
        const [isBankModalVisible, setIsBankModalVisible] = useState(false);
        const [bankData, setBankData] = useState<DataTypeBank[]>([]);
        const [editingBankRecord, setEditingBankRecord] = useState<DataTypeBank | null>(null);
        const [isBankEditMode, setIsBankEditMode] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [isKppModalVisible, setIsKppModalVisible] = useState(false);
        const [kppTargetField, setKppTargetField] = useState<'vat' | 'income_tax' | null>(null);

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const apiUrlNextCode = !isEditModeMaster && backendUrl ? `${backendUrl}/api/suppliers/next-code` : null;
        const { data: dataNextCode, error: errorNextCode, isLoading: isLoadingNextCode } = useSWR<{ nextCode: string }>(apiUrlNextCode, fetcher);

        const apiUrlSupplierData = isEditModeMaster && backendUrl && supplierId ? `${backendUrl}/api/suppliers/${supplierId}` : null;
        const { data: supplierData, error: errorSupplierData, isLoading: isLoadingSupplierData } = useSWR<SupplierFullData>(apiUrlSupplierData, fetcher, {
            revalidateOnFocus: false,
        });

        const apiUrlProvinsi = backendUrl ? `${backendUrl}/api/provinsi` : null;
        const { data: dataProvinsi, error: errorProvinsi, isLoading: isLoadingProvinsi } = useSWR<ProvinsiData[]>(apiUrlProvinsi, fetcher);

        const apiUrlKabupaten = backendUrl && selectedKodeProvinsi
            ? `${backendUrl}/api/kabupaten/${selectedKodeProvinsi}`
            : null;
        const { data: dataKabupaten, error: errorKabupaten, isLoading: isLoadingKabupaten } = useSWR<KabupatenData[]>(apiUrlKabupaten, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKecamatan = backendUrl && selectedKodeDati
            ? `${backendUrl}/api/kecamatan/${selectedKodeDati}`
            : null;
        const { data: dataKecamatan, error: errorKecamatan, isLoading: isLoadingKecamatan } = useSWR<KecamatanData[]>(apiUrlKecamatan, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKelurahan = backendUrl && selectedKodeKecamatan
            ? `${backendUrl}/api/kelurahan/${selectedKodeKecamatan}`
            : null;
        const { data: dataKelurahan, error: errorKelurahan, isLoading: isLoadingKelurahan } = useSWR<KelurahanData[]>(apiUrlKelurahan, fetcher, {
            revalidateOnFocus: false, revalidateOnReconnect: false,
        });

        const apiUrlKabupatenVAT = backendUrl && selectedKodeProvinsiVAT
            ? `${backendUrl}/api/kabupaten/${selectedKodeProvinsiVAT}`
            : null;
        const { data: dataKabupatenVAT, error: errorKabupatenVAT, isLoading: isLoadingKabupatenVAT } = useSWR<KabupatenData[]>(apiUrlKabupatenVAT, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKecamatanVAT = backendUrl && selectedKodeDatiVAT
            ? `${backendUrl}/api/kecamatan/${selectedKodeDatiVAT}`
            : null;
        const { data: dataKecamatanVAT, error: errorKecamatanVAT, isLoading: isLoadingKecamatanVAT } = useSWR<KecamatanData[]>(apiUrlKecamatanVAT, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKelurahanVAT = backendUrl && selectedKodeKecamatanVAT
            ? `${backendUrl}/api/kelurahan/${selectedKodeKecamatanVAT}`
            : null;
        const { data: dataKelurahanVAT, error: errorKelurahanVAT, isLoading: isLoadingKelurahanVAT } = useSWR<KelurahanData[]>(apiUrlKelurahanVAT, fetcher, {
            revalidateOnFocus: false, revalidateOnReconnect: false,
        });

        const apiUrlKabupatenIncomeTax = backendUrl && selectedKodeProvinsiIncomeTax
            ? `${backendUrl}/api/kabupaten/${selectedKodeProvinsiIncomeTax}`
            : null;
        const { data: dataKabupatenIncomeTax, error: errorKabupatenIncomeTax, isLoading: isLoadingKabupatenIncomeTax } = useSWR<KabupatenData[]>(apiUrlKabupatenIncomeTax, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKecamatanIncomeTax = backendUrl && selectedKodeDatiIncomeTax
            ? `${backendUrl}/api/kecamatan/${selectedKodeDatiIncomeTax}`
            : null;
        const { data: dataKecamatanIncomeTax, error: errorKecamatanIncomeTax, isLoading: isLoadingKecamatanIncomeTax } = useSWR<KecamatanData[]>(apiUrlKecamatanIncomeTax, fetcher, {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        });

        const apiUrlKelurahanIncomeTax = backendUrl && selectedKodeKecamatanIncomeTax
            ? `${backendUrl}/api/kelurahan/${selectedKodeKecamatanIncomeTax}`
            : null;
        const { data: dataKelurahanIncomeTax, error: errorKelurahanIncomeTax, isLoading: isLoadingKelurahanIncomeTax } = useSWR<KelurahanData[]>(apiUrlKelurahanIncomeTax, fetcher, {
            revalidateOnFocus: false, revalidateOnReconnect: false,
        });

        const apiUrlVatTrans = backendUrl ? `${backendUrl}/api/vat-trans` : null;
        const { data: dataVatTrans, error: errorVatTrans, isLoading: isLoadingVatTrans } = useSWR<VatTransData[]>(apiUrlVatTrans, fetcher);

        useEffect(() => {
            if (isEditModeMaster && supplierData) {
                form.setFieldsValue({ supplier_code: supplierData.supplier_code });
            } else if (!isEditModeMaster && dataNextCode?.nextCode && !isLoadingNextCode) {
                form.setFieldsValue({ supplier_code: dataNextCode.nextCode });
            }
        }, [isEditModeMaster, supplierData, dataNextCode, isLoadingNextCode, form]);

        useEffect(() => { if (dataKelurahan) setAllKelurahanData(dataKelurahan); }, [dataKelurahan]);
        useEffect(() => { if (dataKelurahanVAT) setAllKelurahanDataVAT(dataKelurahanVAT); }, [dataKelurahanVAT]);
        useEffect(() => { if (dataKelurahanIncomeTax) setAllKelurahanDataIncomeTax(dataKelurahanIncomeTax); }, [dataKelurahanIncomeTax]);

        useEffect(() => {
            if (isEditModeMaster && supplierData && form) {
                const npwpDate = supplierData.npwp_date ? dayjs(supplierData.npwp_date) : null;
                const pkpDate = supplierData.pkp_date ? dayjs(supplierData.pkp_date) : null;
                const incomeTaxNpwpDate = supplierData.income_tax_npwp_date ? dayjs(supplierData.income_tax_npwp_date) : null;
                const incomeTaxPkpDate = supplierData.income_tax_pkp_date ? dayjs(supplierData.income_tax_pkp_date) : null;


                form.setFieldsValue({
                    status: supplierData.status_text,
                    supplier_code: supplierData.supplier_code,
                    supplier_name: supplierData.supplier_name,
                    term_of_payment: supplierData.term_of_payment,
                    id_no: supplierData.id_no,
                    address: supplierData.address,
                    rt_rw: supplierData.rt_rw,
                    provinsi: supplierData.provinsi,
                    kabupaten: supplierData.kabupaten,
                    kecamatan: supplierData.kecamatan,
                    kelurahan: supplierData.kelurahan,
                    kode_pos: supplierData.master_kode_pos || supplierData.kode_pos,
                    phone: supplierData.phone,
                    email: supplierData.email,
                    minimum_down_payment: supplierData.minimum_down_payment,
                    tax_industry: supplierData.tax_industry,
                    discount: supplierData.discount,
                    npwp: supplierData.npwp,
                    npwp_date: npwpDate,
                    pkp_no: supplierData.pkp_no,
                    pkp_date: pkpDate,
                    vat_transaction_code: supplierData.vat_transaction_code,
                    vat_name: supplierData.vat_name,
                    vat_address: supplierData.vat_address,
                    vat_rt_rw: supplierData.vat_rt_rw,
                    vat_provinsi: supplierData.vat_provinsi,
                    vat_kabupaten: supplierData.vat_kabupaten,
                    vat_kecamatan: supplierData.vat_kecamatan,
                    vat_kelurahan: supplierData.vat_kelurahan,
                    vat_kode_pos: supplierData.vat_master_kode_pos || supplierData.vat_kode_pos,
                    kpp_code: supplierData.kpp_code,
                    kpp_name: supplierData.kpp_name,
                    same_as_vat: supplierData.same_as_vat == 1,
                    income_tax_npwp: supplierData.income_tax_npwp,
                    income_tax_npwp_date: incomeTaxNpwpDate,
                    income_tax_pkp_no: supplierData.income_tax_pkp_no,
                    income_tax_pkp_date: incomeTaxPkpDate,
                    income_tax_name: supplierData.income_tax_name,
                    income_tax_address: supplierData.income_tax_address,
                    income_tax_rt_rw: supplierData.income_tax_rt_rw,
                    income_tax_provinsi: supplierData.income_tax_provinsi,
                    income_tax_kabupaten: supplierData.income_tax_kabupaten,
                    income_tax_kecamatan: supplierData.income_tax_kecamatan,
                    income_tax_kelurahan: supplierData.income_tax_kelurahan,
                    income_tax_kode_pos: supplierData.income_tax_master_kode_pos || supplierData.income_tax_kode_pos,
                    income_tax_kpp_code: supplierData.income_tax_kpp_code,
                    income_tax_kpp_name: supplierData.income_tax_kpp_name,
                });

                setSelectedValuePKPvat(supplierData.pkp === 1 ? "Yes" : "No");
                setSelectedValuePKP(supplierData.income_tax_pkp === 1 ? "Yes" : "No");
                if (supplierData.provinsi) setSelectedKodeProvinsi(supplierData.provinsi);
                if (supplierData.kabupaten) setSelectedKodeDati(supplierData.kabupaten);
                if (supplierData.kecamatan) setSelectedKodeKecamatan(supplierData.kecamatan);
                if (supplierData.vat_provinsi) setSelectedKodeProvinsiVAT(supplierData.vat_provinsi);
                if (supplierData.vat_kabupaten) setSelectedKodeDatiVAT(supplierData.vat_kabupaten);
                if (supplierData.vat_kecamatan) setSelectedKodeKecamatanVAT(supplierData.vat_kecamatan);
                if (supplierData.income_tax_provinsi) setSelectedKodeProvinsiIncomeTax(supplierData.income_tax_provinsi);
                if (supplierData.income_tax_kabupaten) setSelectedKodeDatiIncomeTax(supplierData.income_tax_kabupaten);
                if (supplierData.income_tax_kecamatan) setSelectedKodeKecamatanIncomeTax(supplierData.income_tax_kecamatan);

                setCopData(supplierData.cop_details || []);
                setBankData(supplierData.bank_details || []);

                if (supplierData.id_no || supplierData.address || supplierData.rt_rw || supplierData.provinsi || supplierData.kabupaten || supplierData.kecamatan || supplierData.kelurahan || supplierData.phone || supplierData.email) {
                    setShowContentProfiel(true);
                }

                if (supplierData.minimum_down_payment !== null || supplierData.tax_industry !== null || supplierData.discount) {
                    setShowContentStatus(true);
                }

                if (supplierData.npwp || supplierData.npwp_date || supplierData.pkp_no || supplierData.pkp_date || supplierData.vat_transaction_code || supplierData.vat_name || supplierData.vat_address || supplierData.vat_rt_rw || supplierData.vat_provinsi || supplierData.kpp_code || supplierData.kpp_name) {
                    setShowContentVAT(true);
                }

                if (supplierData.same_as_vat == 1 || supplierData.income_tax_npwp || supplierData.income_tax_npwp_date || supplierData.income_tax_pkp_no || supplierData.income_tax_pkp_date || supplierData.income_tax_name || supplierData.income_tax_address || supplierData.income_tax_rt_rw || supplierData.income_tax_provinsi || supplierData.income_tax_kpp_code || supplierData.income_tax_kpp_name) {
                    setShowContentIncomeTax(true);
                }

                if (supplierData.cop_details && supplierData.cop_details.length > 0) {
                    setShowContentContactorPIC(true);
                }

                if (supplierData.bank_details && supplierData.bank_details.length > 0) {
                    setShowContentBank(true);
                }

            } else if (!isEditModeMaster) {
                form.setFieldsValue({ status: "Active" });
                setSelectedValuePKP(null);
                setSelectedValuePKPvat(null);
                setCopData([]);
                setBankData([]);
                setShowContentProfiel(false);
                setShowContentStatus(false);
                setShowContentVAT(false);
                setShowContentIncomeTax(false);
                setShowContentContactorPIC(false);
                setShowContentBank(false);
                setSelectedKodeProvinsi(null);
                setSelectedKodeDati(null);
                setSelectedKodeKecamatan(null);
                setSelectedKodeProvinsiVAT(null);
            }
        }, [isEditModeMaster, supplierData, form]);

        useEffect(() => {
            if (dataKelurahanVAT) {
                setAllKelurahanDataVAT(dataKelurahanVAT);
            }
        }, [dataKelurahanVAT]);

        useEffect(() => {
            if (dataNextCode?.nextCode && !isLoadingNextCode && !errorNextCode) {
                form.setFieldsValue({ supplier_code: dataNextCode.nextCode });
            }
        }, [dataNextCode, isLoadingNextCode, errorNextCode, form]);

        useEffect(() => {
            if (dataKelurahan) {
                setAllKelurahanData(dataKelurahan);
            }
        }, [dataKelurahan]);

        useEffect(() => {
            if (dataKelurahanIncomeTax) {
                setAllKelurahanDataIncomeTax(dataKelurahanIncomeTax);
            }
        }, [dataKelurahanIncomeTax]);

        if (errorNextCode) console.error("Error loading next supplier code:", errorNextCode);
        if (errorSupplierData) console.error(`Error loading supplier data for ID ${supplierId}:`, errorSupplierData);
        if (errorProvinsi) console.error("Error loading provinsi data:", errorProvinsi);
        if (errorKabupaten) console.error(`Error loading kabupaten data for provinsi ${selectedKodeProvinsi}:`, errorKabupaten);
        if (errorKecamatan) console.error(`Error loading kecamatan data for kabupaten ${selectedKodeDati}:`, errorKecamatan);
        if (errorKelurahan) console.error(`Error loading kelurahan data for kecamatan ${selectedKodeKecamatan}:`, errorKelurahan);
        if (errorKabupatenVAT) console.error(`Error loading kabupaten VAT data for provinsi ${selectedKodeProvinsiVAT}:`, errorKabupatenVAT);
        if (errorKecamatanVAT) console.error(`Error loading kecamatan VAT data for kabupaten ${selectedKodeDatiVAT}:`, errorKecamatanVAT);
        if (errorKelurahanVAT) console.error(`Error loading kelurahan VAT data for kecamatan ${selectedKodeKecamatanVAT}:`, errorKelurahanVAT);
        if (errorKabupatenIncomeTax) console.error(`Error loading income tax kabupaten data for provinsi ${selectedKodeProvinsiIncomeTax}:`, errorKabupatenIncomeTax);
        if (errorKecamatanIncomeTax) console.error(`Error loading income tax kecamatan data for kabupaten ${selectedKodeDatiIncomeTax}:`, errorKecamatanIncomeTax);
        if (errorKelurahanIncomeTax) console.error(`Error loading income tax kelurahan data for kecamatan ${selectedKodeKecamatanIncomeTax}:`, errorKelurahanIncomeTax);
        if (errorVatTrans) console.error("Error loading VAT Transaction data:", errorVatTrans);

        const transformStyle = {
            transform: "scale(0.80)",
            transformOrigin: "top left",
            width: "125%",
        };

        const formInitialValues = isEditModeMaster ? {} : { status: "Active" };;

        const onChangeSelect = (value: string) => { console.log(`Selected: ${value}`); };
        const onDateChange: DatePickerProps['onChange'] = (date, dateString) => { console.log("Selected Date: ", dateString); };
        const handleCheckboxChangePKP = (value: string) => { setSelectedValuePKP((prevValue) => (prevValue === value ? null : value)); };
        const handleCheckboxChangePKPvat = (value: string) => { setSelectedValuePKPvat((prevValue) => (prevValue === value ? null : value)); };

        const handleProvinsiChange = useCallback((value: string) => {
            setSelectedKodeProvinsi(value);
            setSelectedKodeDati(null);
            setSelectedKodeKecamatan(null);
            setAllKelurahanData([]);
            form.setFieldsValue({ kabupaten: null, kecamatan: null, kelurahan: null, kode_pos: null });
        }, [form]);

        const handleKabupatenChange = useCallback((value: string) => {
            setSelectedKodeDati(value);
            setSelectedKodeKecamatan(null);
            setAllKelurahanData([]);
            form.setFieldsValue({ kecamatan: null, kelurahan: null, kode_pos: null });
        }, [form]);

        const handleKecamatanChange = useCallback((value: string) => {
            setSelectedKodeKecamatan(value);
            setAllKelurahanData([]);
            form.setFieldsValue({ kelurahan: null, kode_pos: null });
        }, [form]);

        const handleKelurahanChange = useCallback((value: string) => {
            const selectedKelurahan = allKelurahanData.find(kel => kel.kode_kelurahan === value);
            form.setFieldsValue({ kode_pos: selectedKelurahan ? selectedKelurahan.kode_pos : null });
        }, [allKelurahanData, form]);

        const handleProvinsiChangeVAT = useCallback((value: string) => {
            setSelectedKodeProvinsiVAT(value);
            setSelectedKodeDatiVAT(null);
            setSelectedKodeKecamatanVAT(null);
            setAllKelurahanDataVAT([]);
            form.setFieldsValue({ vat_kabupaten: null, vat_kecamatan: null, vat_kelurahan: null, vat_kode_pos: null });
        }, [form]);

        const handleKabupatenChangeVAT = (value: string) => {
            setSelectedKodeDatiVAT(value);
            setSelectedKodeKecamatanVAT(null);
            setAllKelurahanDataVAT([]);
            form.setFieldsValue({
                vat_kecamatan: null,
                vat_kelurahan: null,
                vat_kode_pos: null
            });
        };

        const handleKecamatanChangeVAT = (value: string) => {
            setSelectedKodeKecamatanVAT(value);
            setAllKelurahanDataVAT([]);
            form.setFieldsValue({
                vat_kelurahan: null,
                vat_kode_pos: null
            });
        };

        const handleKelurahanChangeVAT = useCallback((value: string) => {
            const selectedKelurahanVAT = allKelurahanDataVAT.find(kel => kel.kode_kelurahan === value);
            form.setFieldsValue({ vat_kode_pos: selectedKelurahanVAT ? selectedKelurahanVAT.kode_pos : null });
        }, [allKelurahanDataVAT, form]);


        const handleProvinsiChangeIncomeTax = useCallback((value: string) => {
            setSelectedKodeProvinsiIncomeTax(value);
            setSelectedKodeDatiIncomeTax(null);
            setSelectedKodeKecamatanIncomeTax(null);
            setAllKelurahanDataIncomeTax([]);
            form.setFieldsValue({ income_tax_kabupaten: null, income_tax_kecamatan: null, income_tax_kelurahan: null, income_tax_kode_pos: null });
        }, [form]);

        const handleKabupatenChangeIncomeTax = (value: string) => {
            setSelectedKodeDatiIncomeTax(value);
            setSelectedKodeKecamatanIncomeTax(null);
            setAllKelurahanDataIncomeTax([]);
            form.setFieldsValue({
                income_tax_kecamatan: null,
                income_tax_kelurahan: null,
                income_tax_kode_pos: null
            });
        };

        const handleKecamatanChangeIncomeTax = (value: string) => {
            setSelectedKodeKecamatanIncomeTax(value);
            setAllKelurahanDataIncomeTax([]);
            form.setFieldsValue({
                income_tax_kelurahan: null,
                income_tax_kode_pos: null
            });
        };

        const handleKelurahanChangeIncomeTax = useCallback((value: string) => {
            const selectedKelurahanIncomeTax = allKelurahanDataIncomeTax.find(kel => kel.kode_kelurahan === value);
            form.setFieldsValue({ income_tax_kode_pos: selectedKelurahanIncomeTax ? selectedKelurahanIncomeTax.kode_pos : null });
        }, [allKelurahanDataIncomeTax, form]);

        const showCopModal = () => {
            setIsEditMode(false);
            setEditingCopRecord(null);
            setIsCopModalVisible(true);
        };

        const handleCopModalCancel = () => {
            setIsCopModalVisible(false);
            setIsEditMode(false);
            setEditingCopRecord(null);

        };

        const handleCopModalOk = (values: any) => {
            if (isEditMode && editingCopRecord) {

                setCopData(prevData =>
                    prevData.map(item =>
                        item.key === editingCopRecord.key
                            ? {
                                ...item,
                                contact_name: values.contact_name,
                                devision: values.devision,
                                job_title: values.job_title,
                                phone_no: values.phone_no,

                            }
                            : item
                    )
                );
            } else {

                const newEntry: DataTypeCOP = {
                    key: Date.now(),
                    contact_name: values.contact_name,
                    devision: values.devision,
                    job_title: values.job_title,
                    phone_no: values.phone_no,
                    status: 'Active',
                };
                setCopData(prevData => [...prevData, newEntry]);
            }

            handleCopModalCancel();
        };

        const handleCopRowDoubleClick = (record: DataTypeCOP) => {
            setIsEditMode(true);
            setEditingCopRecord(record);
            setIsCopModalVisible(true);
        };

        const handleActivateCop = () => {
            if (selectedRowKeysCOP.length === 0) return;
            const updatedData = copData.map(item => {
                if (selectedRowKeysCOP.includes(item.key)) {
                    return { ...item, status: 'Active' };
                }
                return item;
            });
            setCopData(updatedData);
            setSelectedRowKeysCOP([]);
        };

        const handleDeactivateCop = () => {
            if (selectedRowKeysCOP.length === 0) return;
            const updatedData = copData.map(item => {
                if (selectedRowKeysCOP.includes(item.key)) {
                    return { ...item, status: 'Deactive' };
                }
                return item;
            });
            setCopData(updatedData);
            setSelectedRowKeysCOP([]);
        };

        const showBankModal = () => {
            setIsBankEditMode(false);
            setEditingBankRecord(null);
            setIsBankModalVisible(true);
        };

        const handleBankModalCancel = () => {
            setIsBankModalVisible(false);
            setIsBankEditMode(false);
            setEditingBankRecord(null);
        };

        const handleBankModalOk = (values: any) => {
            if (isBankEditMode && editingBankRecord) {

                setBankData(prevData =>
                    prevData.map(item =>
                        item.key === editingBankRecord.key
                            ? {
                                ...item,
                                bank_name: values.bank_name,
                                acc_type: values.acc_type,
                                account_no: values.account_no,
                                name: values.name,
                            }
                            : item
                    )
                );
            } else {

                const newEntry: DataTypeBank = {
                    key: Date.now(),
                    bank_name: values.bank_name,
                    acc_type: values.acc_type,
                    account_no: values.account_no,
                    name: values.name,
                    status: 'Active',
                };
                setBankData(prevData => [...prevData, newEntry]);
            }

            handleBankModalCancel();
        };

        const handleBankRowDoubleClick = (record: DataTypeBank) => {
            setIsBankEditMode(true);
            setEditingBankRecord(record);
            setIsBankModalVisible(true);
        };

        const handleActivateBank = () => {
            if (selectedRowKeysBank.length === 0) return;
            const updatedData = bankData.map(item => {
                if (selectedRowKeysBank.includes(item.key)) {
                    return { ...item, status: 'Active' };
                }
                return item;
            });
            setBankData(updatedData);
            setSelectedRowKeysBank([]);
        };

        const handleDeactivateBank = () => {
            if (selectedRowKeysBank.length === 0) return;
            const updatedData = bankData.map(item => {
                if (selectedRowKeysBank.includes(item.key)) {
                    return { ...item, status: 'Deactive' };
                }
                return item;
            });
            setBankData(updatedData);
            setSelectedRowKeysBank([]);
        };

        const showKppModal = (target: 'vat' | 'income_tax') => {
            setKppTargetField(target); // Simpan target field
            setIsKppModalVisible(true);
        };

        const handleKppModalCancel = () => {
            setIsKppModalVisible(false);
            setKppTargetField(null); // Reset target field saat cancel
        };

        const handleKppModalOk = (selectedKpp: { kpp_code: string; kpp_name: string }) => {
            if (selectedKpp && kppTargetField) {
                if (kppTargetField === 'vat') {
                    form.setFieldsValue({
                        kpp_code: selectedKpp.kpp_code,
                        kpp_name: selectedKpp.kpp_name,
                    });
                } else if (kppTargetField === 'income_tax') {
                    form.setFieldsValue({
                        income_tax_kpp_code: selectedKpp.kpp_code,
                        income_tax_kpp_name: selectedKpp.kpp_name,
                    });
                }
            } else {
                console.warn('No KPP selected or target field is not set.');
            }
            setIsKppModalVisible(false);
            setKppTargetField(null); // Reset target field setelah OK
        };

        const handleSave = async () => {
            setIsSaving(true);
            const loadingMessageKey = 'saving';
            message.loading({ content: 'Menyimpan data...', key: loadingMessageKey });

            try {
                const formValues = await form.validateFields();
                const basePayload = {
                    ...formValues,

                    npwp_date: formValues.npwp_date ? dayjs(formValues.npwp_date).format('YYYY-MM-DD') : null,
                    pkp_date: formValues.pkp_date ? dayjs(formValues.pkp_date).format('YYYY-MM-DD') : null,
                    income_tax_npwp_date: formValues.income_tax_npwp_date ? dayjs(formValues.income_tax_npwp_date).format('YYYY-MM-DD') : null,
                    income_tax_pkp_date: formValues.income_tax_pkp_date ? dayjs(formValues.income_tax_pkp_date).format('YYYY-MM-DD') : null,
                    pkp: selectedValuePKPvat === 'Yes' ? 1 : 0,
                    income_tax_pkp: selectedValuePKP === 'Yes' ? 1 : 0,
                    same_as_vat: formValues.same_as_vat ? 1 : 0,
                    discount: formValues.discount || null,
                    status: isEditModeMaster && supplierData ? supplierData.status : 1,
                };

                let saveMasterUrl: string | null = null;
                let httpMethod: 'POST' | 'PUT' = 'POST';

                if (isEditModeMaster && supplierId) {

                    saveMasterUrl = backendUrl ? `${backendUrl}/api/suppliers/${supplierId}` : null;
                    httpMethod = 'PUT';
                } else {
                    saveMasterUrl = backendUrl ? `${backendUrl}/api/suppliers` : null;
                    httpMethod = 'POST';
                }
                if (!saveMasterUrl) {
                    throw new Error("Backend URL not configured.");
                }
                const masterResponse = await fetch(saveMasterUrl, {
                    method: httpMethod,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(basePayload),
                });

                if (!masterResponse.ok) {
                    const errorData = await masterResponse.json();
                    throw new Error(errorData.message || `Failed to ${httpMethod === 'POST' ? 'save' : 'update'} supplier master: ${masterResponse.statusText}`);
                }

                const masterResult = await masterResponse.json();

                const currentSupplierId = isEditModeMaster ? supplierId : masterResult.id;
                if (copData.length > 0 && currentSupplierId) {
                    const saveCopUrl = backendUrl ? `${backendUrl}/api/suppliers/cop?supplierId=${currentSupplierId}` : null;
                    if (!saveCopUrl) throw new Error("Backend URL not configured for COP.");

                    const copResponse = await fetch(saveCopUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(copData),
                    });

                    if (!copResponse.ok) {
                        const errorData = await copResponse.json();
                        message.error({ content: `Supplier ${httpMethod === 'POST' ? 'saved' : 'updated'}, but failed to save COP data: ${errorData.message || copResponse.statusText}`, key: loadingMessageKey, duration: 5 });
                    } else {
                    }
                }

                if (bankData.length > 0 && currentSupplierId) {
                    const saveBankUrl = backendUrl ? `${backendUrl}/api/suppliers/bank?supplierId=${currentSupplierId}` : null;
                    if (!saveBankUrl) throw new Error("Backend URL not configured for Bank.");
                    const bankResponse = await fetch(saveBankUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(bankData),
                    });

                    if (!bankResponse.ok) {
                        const errorData = await bankResponse.json();
                        message.error({ content: `Supplier ${httpMethod === 'POST' ? 'saved' : 'updated'}, but failed to save Bank data: ${errorData.message || bankResponse.statusText}`, key: loadingMessageKey, duration: 5 });
                    } else {
                    }
                }

                message.success({ content: `Supplier data ${httpMethod === 'POST' ? 'saved' : 'updated'} successfully!`, key: loadingMessageKey, duration: 2 });

                if (httpMethod === 'POST') {
                    form.resetFields();
                    setCopData([]);
                    setBankData([]);
                    setSelectedValuePKP(null);
                    setSelectedValuePKPvat(null);
                } else {
                }

            } catch (error: any) {
                console.error("Save/Update failed:", error);
                if (error.errorFields) {
                    message.error({ content: 'Please fill all required fields correctly.', key: loadingMessageKey, duration: 3 });
                } else {
                    message.error({ content: `Save/Update failed: ${error.message}`, key: loadingMessageKey, duration: 3 });
                }
            } finally {
                setIsSaving(false);
            }
        };

        const columnsCOP: TableColumnsType<DataTypeCOP> = [
            { title: "Contact Name", dataIndex: "contact_name", key: "contact_name" },
            { title: "Division", dataIndex: "devision", key: "devision" },
            { title: "Job Title", dataIndex: "job_title", key: "job_title" },
            { title: "Phone No", dataIndex: "phone_no", key: "phone_no" },
            { title: "Status", dataIndex: "status", key: "status" },
        ];

        const rowSelectionCOP: TableProps<DataTypeCOP>["rowSelection"] = {
            selectedRowKeys: selectedRowKeysCOP,
            onChange: (newSelectedRowKeys) => setSelectedRowKeysCOP(newSelectedRowKeys),
        };

        const columnsBank: TableColumnsType<DataTypeBank> = [
            { title: "Bank Name", dataIndex: "bank_name", key: "bank_name" },
            { title: "Acc Type", dataIndex: "acc_type", key: "acc_type" },
            { title: "Account No", dataIndex: "account_no", key: "account_no" },
            { title: "Name", dataIndex: "name", key: "name" },
            { title: "Status", dataIndex: "status", key: "status" },
        ];

        const rowSelectionBank: TableProps<DataTypeBank>["rowSelection"] = {
            selectedRowKeys: selectedRowKeysBank,
            onChange: (newSelectedRowKeys) => setSelectedRowKeysBank(newSelectedRowKeys),
        };

        const provinsiOptions = dataProvinsi?.map(prov => ({ value: prov.kode_provinsi, label: prov.nama_provinsi })) || [];
        const kabupatenOptions = dataKabupaten?.map(kab => ({ value: kab.kode_dati, label: kab.deskripsi_kode_dati })) || [];
        const kecamatanOptions = dataKecamatan?.map(kec => ({ value: kec.kode_kecamatan, label: kec.deskripsi_kode_kecamatan })) || [];
        const kelurahanOptions = allKelurahanData?.map(kel => ({ value: kel.kode_kelurahan, label: kel.deskripsi_kode_kelurahan })) || [];


        const kabupatenOptionsVAT = dataKabupatenVAT?.map(kab => ({ value: kab.kode_dati, label: kab.deskripsi_kode_dati })) || [];
        const kecamatanOptionsVAT = dataKecamatanVAT?.map(kec => ({ value: kec.kode_kecamatan, label: kec.deskripsi_kode_kecamatan })) || [];
        const kelurahanOptionsVAT = allKelurahanDataVAT?.map(kel => ({ value: kel.kode_kelurahan, label: kel.deskripsi_kode_kelurahan })) || [];

        const kabupatenOptionsIncomeTax = dataKabupatenIncomeTax?.map(kab => ({ value: kab.kode_dati, label: kab.deskripsi_kode_dati })) || [];
        const kecamatanOptionsIncomeTax = dataKecamatanIncomeTax?.map(kec => ({ value: kec.kode_kecamatan, label: kec.deskripsi_kode_kecamatan })) || [];
        const kelurahanOptionsIncomeTax = allKelurahanDataIncomeTax?.map(kel => ({ value: kel.kode_kelurahan, label: kel.deskripsi_kode_kelurahan })) || [];

        const vatTransOptions = dataVatTrans?.map(vt => ({ value: vt.code, label: vt.deskripsi })) || [];

        const isCopActionButtonDisabled = selectedRowKeysCOP.length === 0;
        const isBankActionButtonDisabled = selectedRowKeysBank.length === 0;


        const propsToPass: SupplierMasterProps = {
            ...originalProps,
            transformStyle,
            showContentProfiel,
            setShowContentProfiel,
            showContentStatus,
            setShowContentStatus,
            showContentVAT,
            setShowContentVAT,
            showContentIncomeTax,
            setShowContentIncomeTax,
            showContentContactorPIC,
            setShowContentContactorPIC,
            showContentBank,
            setShowContentBank,
            onDateChange,
            onChangeSelect,
            selectedValuePKP,
            handleCheckboxChangePKP,
            selectedValuePKPvat,
            handleCheckboxChangePKPvat,
            columnsCOP,
            rowSelectionCOP,
            selectedRowKeysCOP,
            copData,
            columnsBank,
            rowSelectionBank,
            selectedRowKeysBank,
            bankData,
            formInitialValues,
            nextSupplierCode: !isEditModeMaster ? dataNextCode?.nextCode : undefined,
            isLoadingNextCode: isLoadingNextCode,
            errorNextCode: errorNextCode,
            form,
            provinsiOptions,
            isLoadingProvinsi,
            errorProvinsi,
            onProvinsiChange: handleProvinsiChange,
            kabupatenOptions,
            isLoadingKabupaten,
            errorKabupaten,
            selectedKodeProvinsi,
            onKabupatenChange: handleKabupatenChange,
            kecamatanOptions,
            isLoadingKecamatan,
            errorKecamatan,
            selectedKodeDati,
            onKecamatanChange: handleKecamatanChange,
            kelurahanOptions,
            isLoadingKelurahan,
            errorKelurahan,
            selectedKodeKecamatan,
            onKelurahanChange: handleKelurahanChange,
            onProvinsiChangeVAT: handleProvinsiChangeVAT,
            kabupatenOptionsVAT,
            isLoadingKabupatenVAT,
            errorKabupatenVAT,
            selectedKodeProvinsiVAT,
            onKabupatenChangeVAT: handleKabupatenChangeVAT,
            kecamatanOptionsVAT,
            isLoadingKecamatanVAT,
            errorKecamatanVAT,
            selectedKodeDatiVAT,
            onKecamatanChangeVAT: handleKecamatanChangeVAT,
            kelurahanOptionsVAT,
            isLoadingKelurahanVAT,
            errorKelurahanVAT,
            selectedKodeKecamatanVAT,
            onKelurahanChangeVAT: handleKelurahanChangeVAT,
            onProvinsiChangeIncomeTax: handleProvinsiChangeIncomeTax,
            kabupatenOptionsIncomeTax,
            isLoadingKabupatenIncomeTax,
            errorKabupatenIncomeTax,
            selectedKodeProvinsiIncomeTax,
            onKabupatenChangeIncomeTax: handleKabupatenChangeIncomeTax,
            kecamatanOptionsIncomeTax,
            isLoadingKecamatanIncomeTax,
            errorKecamatanIncomeTax,
            selectedKodeDatiIncomeTax,
            onKecamatanChangeIncomeTax: handleKecamatanChangeIncomeTax,
            kelurahanOptionsIncomeTax,
            isLoadingKelurahanIncomeTax,
            errorKelurahanIncomeTax,
            selectedKodeKecamatanIncomeTax,
            onKelurahanChangeIncomeTax: handleKelurahanChangeIncomeTax,
            vatTransOptions,
            isLoadingVatTrans,
            errorVatTrans,
            isCopModalVisible,
            showCopModal,
            handleCopModalCancel,
            handleCopModalOk,
            handleCopRowDoubleClick,
            isEditMode,
            editingCopRecord,
            handleActivateCop,
            handleDeactivateCop,
            isCopActionButtonDisabled,
            isBankModalVisible,
            showBankModal,
            handleBankModalCancel,
            handleBankModalOk,
            handleBankRowDoubleClick,
            isBankEditMode,
            editingBankRecord,
            handleActivateBank,
            handleDeactivateBank,
            isBankActionButtonDisabled,
            handleSave,
            isSaving,
            isEditModeMaster,
            supplierId,
            isLoadingSupplierData,
            isKppModalVisible,
            showKppModal,
            handleKppModalCancel,
            handleKppModalOk,
            kppTargetField,
        };
        if (isEditModeMaster && isLoadingSupplierData) {
            return <div>Loading supplier data...</div>;
        }
        if (isEditModeMaster && errorSupplierData) {
            return <Alert message="Error" description={`Gagal memuat data supplier: ${errorSupplierData.message}`} type="error" showIcon />;
        }

        return <WrappedComponent {...originalProps} {...propsToPass} />;
    };

    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    EnhancedComponent.displayName = `withSupplierMasterLogic(${displayName})`;

    return EnhancedComponent;
};
